/**
 * WebSocket Hook for Real-time Chat
 * Provides stable real-time connection with automatic reconnection
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

export const WS_STATE = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error',
};

const WS_CONFIG = {
  reconnectIntervalBase: 1000,
  reconnectIntervalMax: 30000,
  reconnectAttempts: 10,
  heartbeatInterval: 25000,
  heartbeatTimeout: 10000,
};

const getWebSocketUrl = (token) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  let wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
  wsUrl = wsUrl.replace(/\/api\/?$/, '');
  return `${wsUrl}/ws/chat/${token}`;
};

export const useWebSocket = () => {
  const { token, user } = useAuth();
  const [connectionState, setConnectionState] = useState(WS_STATE.DISCONNECTED);
  const [lastMessage, setLastMessage] = useState(null);
  const [error, setError] = useState(null);
  
  const wsRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const manualDisconnectRef = useRef(false);
  const timersRef = useRef({});
  const handlersRef = useRef(new Map());
  const tokenRef = useRef(token);
  const connectFnRef = useRef(null);

  useEffect(() => { tokenRef.current = token; }, [token]);

  const clearTimers = useCallback(() => {
    Object.values(timersRef.current).forEach(t => t && clearTimeout(t));
    timersRef.current = {};
  }, []);

  // Store connect function in ref to allow recursive calls
  useEffect(() => {
    connectFnRef.current = (isReconnect = false) => {
      const wsToken = tokenRef.current;
      if (!wsToken || manualDisconnectRef.current) return;
      if (wsRef.current?.readyState === WebSocket.OPEN) return;

      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }

      setConnectionState(isReconnect ? WS_STATE.RECONNECTING : WS_STATE.CONNECTING);
      setError(null);

      try {
        const ws = new WebSocket(getWebSocketUrl(wsToken));
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('WebSocket: Connected');
          setConnectionState(WS_STATE.CONNECTED);
          reconnectAttemptRef.current = 0;
          
          // Heartbeat
          if (timersRef.current.heartbeat) clearInterval(timersRef.current.heartbeat);
          timersRef.current.heartbeat = setInterval(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: 'ping' }));
              timersRef.current.heartbeatTimeout = setTimeout(() => {
                wsRef.current?.close();
              }, WS_CONFIG.heartbeatTimeout);
            }
          }, WS_CONFIG.heartbeatInterval);
        };

        ws.onclose = () => {
          clearTimers();
          if (manualDisconnectRef.current) {
            setConnectionState(WS_STATE.DISCONNECTED);
            return;
          }

          if (reconnectAttemptRef.current < WS_CONFIG.reconnectAttempts) {
            const delay = Math.min(
              WS_CONFIG.reconnectIntervalBase * Math.pow(2, reconnectAttemptRef.current),
              WS_CONFIG.reconnectIntervalMax
            ) * (0.8 + Math.random() * 0.4);
            
            setConnectionState(WS_STATE.RECONNECTING);
            reconnectAttemptRef.current++;
            
            timersRef.current.reconnect = setTimeout(() => {
              connectFnRef.current?.(true);
            }, delay);
          } else {
            setConnectionState(WS_STATE.ERROR);
            setError('Connection lost. Please refresh.');
          }
        };

        ws.onerror = () => setError('Connection error');

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            if (data.type === 'pong') {
              clearTimeout(timersRef.current.heartbeatTimeout);
              return;
            }
            if (data.type === 'connected') return;

            setLastMessage(data);
            handlersRef.current.get(data.type)?.forEach(h => {
              try { h(data); } catch (e) { /* ignore */ }
            });
          } catch (e) { /* ignore */ }
        };
      } catch (e) {
        setConnectionState(WS_STATE.ERROR);
        setError('Failed to connect');
      }
    };
  }, [clearTimers]);

  const connect = useCallback(() => {
    manualDisconnectRef.current = false;
    reconnectAttemptRef.current = 0;
    connectFnRef.current?.();
  }, []);

  const disconnect = useCallback(() => {
    manualDisconnectRef.current = true;
    clearTimers();
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close(1000);
      wsRef.current = null;
    }
    setConnectionState(WS_STATE.DISCONNECTED);
  }, [clearTimers]);

  const sendMessage = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
      return true;
    }
    return false;
  }, []);

  const onMessage = useCallback((type, handler) => {
    if (!handlersRef.current.has(type)) handlersRef.current.set(type, []);
    handlersRef.current.get(type).push(handler);
    return () => {
      const handlers = handlersRef.current.get(type);
      if (handlers) {
        const idx = handlers.indexOf(handler);
        if (idx > -1) handlers.splice(idx, 1);
      }
    };
  }, []);

  const sendChatMessage = useCallback((matchId, content) => 
    sendMessage({ type: 'message', match_id: matchId, content }), [sendMessage]);
  
  const sendTypingIndicator = useCallback((matchId) => 
    sendMessage({ type: 'typing', match_id: matchId }), [sendMessage]);
  
  const sendReadReceipt = useCallback((matchId) => 
    sendMessage({ type: 'read', match_id: matchId }), [sendMessage]);

  // Auto-connect
  useEffect(() => {
    if (token && user?.id) {
      manualDisconnectRef.current = false;
      // Small delay to ensure connectFnRef is set
      const t = setTimeout(() => connectFnRef.current?.(), 0);
      return () => clearTimeout(t);
    }
  }, [token, user?.id]);

  // Cleanup
  useEffect(() => {
    return () => {
      manualDisconnectRef.current = true;
      clearTimers();
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [clearTimers]);

  // Visibility/online handlers
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && 
          !manualDisconnectRef.current && 
          wsRef.current?.readyState !== WebSocket.OPEN) {
        reconnectAttemptRef.current = 0;
        connectFnRef.current?.();
      }
    };
    const handleOnline = () => {
      if (!manualDisconnectRef.current && 
          wsRef.current?.readyState !== WebSocket.OPEN) {
        reconnectAttemptRef.current = 0;
        connectFnRef.current?.();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  return {
    connectionState,
    isConnected: connectionState === WS_STATE.CONNECTED,
    error,
    lastMessage,
    connect,
    disconnect,
    sendMessage,
    sendChatMessage,
    sendTypingIndicator,
    sendReadReceipt,
    onMessage,
  };
};

export default useWebSocket;
